import { useQuery } from '@tanstack/react-query'
import type { LanguageType } from '@repo/sdk'
import { ArrowLeft, ChevronDown, Loader2 } from 'lucide-react'
import { useEffect, useRef } from 'react'
import ReactMarkdown from 'react-markdown'
import { useLocation, useNavigate } from 'react-router-dom'

import { useI18n } from '@/lib/i18n'
import { useTRPC } from '@/lib/trpc'
import { withLanguagePath } from '@/lib/url-language-utils'

const sectionIds = [
  'eventRules',
  'configuration',
  'roleSkills',
  'nightSequence',
  'victoryConditions',
  'sheriffRules',
  'informationDisclosure',
] as const
const hallSectionIds = [
  'roleSkills',
  'nightSequence',
  'victoryConditions',
  'sheriffRules',
] as const

type RulesViewerProps = { editionId: string } & (
  | { variant: 'hall', onFullRules: () => void, focusFullRules?: boolean }
  | { variant?: 'full', onBack: () => void }
)

export function TournamentRulesViewer(props: RulesViewerProps) {
  const { editionId } = props
  const { language, t } = useI18n()
  const trpc = useTRPC()
  const location = useLocation()
  const navigate = useNavigate()
  const contentRef = useRef<HTMLDivElement>(null)
  const backRef = useRef<HTMLButtonElement>(null)
  const fullRulesRef = useRef<HTMLButtonElement>(null)
  const focusFullRules = props.variant === 'hall' && props.focusFullRules
  const restoreFullRulesFocus = useRef(focusFullRules)
  const rulesQuery = useQuery({
    ...trpc.tournamentRouter.getRules.queryOptions({
      editionId,
      language: language as LanguageType,
    }),
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  })
  const receivedRules
    = rulesQuery.data?.editionId === editionId && !rulesQuery.isError
      ? rulesQuery.data
      : undefined
  const rules
    = receivedRules?.contentLanguage === language
      && (rulesQuery.isFetchedAfterMount || !rulesQuery.isFetching)
      ? receivedRules
      : undefined
  const requestedSection = new URLSearchParams(location.search).get(
    'tournamentSection',
  )
  const selectedSection
    = sectionIds.find(id => id === requestedSection) ?? 'eventRules'

  useEffect(() => {
    backRef.current?.focus({ preventScroll: true })
  }, [])

  useEffect(() => {
    restoreFullRulesFocus.current = focusFullRules
  }, [focusFullRules])

  useEffect(() => {
    if (rules && restoreFullRulesFocus.current && fullRulesRef.current) {
      fullRulesRef.current?.focus({ preventScroll: true })
      restoreFullRulesFocus.current = false
    }
  }, [rules, focusFullRules])

  useEffect(() => {
    if (
      receivedRules
      && !rulesQuery.isFetching
      && receivedRules.contentLanguage !== language
    ) {
      window.location.replace(
        `${window.location.origin}${withLanguagePath(window.location.pathname, receivedRules.contentLanguage)}${window.location.search}${window.location.hash}`,
      )
    }
  }, [receivedRules, rulesQuery.isFetching, language])

  useEffect(() => {
    if (!rules)
      return
    const section = contentRef.current?.querySelector<HTMLDetailsElement>(
      `[data-section="${selectedSection === 'configuration' ? 'eventRules' : selectedSection}"]`,
    )
    if (section) {
      section.open = true
      section.scrollIntoView({ block: 'nearest' })
    }
  }, [rules, selectedSection])

  const selectSection = (id: string) => {
    const search = new URLSearchParams(location.search)
    search.set('tournamentSection', id)
    navigate(
      {
        pathname: location.pathname,
        search: `?${search}`,
        hash: location.hash,
      },
      { replace: true },
    )
  }
  const godRoleCategory = rules?.configuration.categories.find(category =>
    category.allowedRoles.some(role =>
      ['psychic', 'guard', 'hunter', 'witch'].includes(role),
    ),
  )
  const mechanicalWolfCategory = rules?.configuration.categories.find(
    category => category.allowedRoles.includes('mechanical_wolf'),
  )
  const visibleSectionIds
    = props.variant === 'hall'
      ? hallSectionIds
      : sectionIds.filter(id => id !== 'configuration')

  return (
    <div
      data-testid="tournament-rules-viewer"
      data-edition-id={editionId}
      data-variant={props.variant ?? 'full'}
      ref={contentRef}
      className={props.variant === 'hall' ? '' : 'mt-5'}
    >
      {props.variant !== 'hall' && (
        <button
          ref={backRef}
          type="button"
          onClick={props.onBack}
          data-testid="tournament-rules-back"
          className="mb-4 flex min-h-11 items-center gap-2 rounded-md px-2 font-semibold text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ArrowLeft className="size-5" aria-hidden="true" />
          {t('tournamentRules.backToTournament')}
        </button>
      )}
      {!rulesQuery.isError && !rules && (
        <p
          role="status"
          data-testid="tournament-rules-loading"
          className="flex items-center justify-center gap-2 py-8"
        >
          <Loader2 className="size-5 animate-spin" aria-hidden="true" />
          {t('forum.loading')}
        </p>
      )}
      {rulesQuery.isError && (
        <div
          role="alert"
          data-testid="tournament-rules-error"
          className="text-center"
        >
          <p>{t('forum.errorGeneric')}</p>
          <button
            type="button"
            data-testid="tournament-rules-retry"
            onClick={() => {
              void rulesQuery.refetch()
            }}
            className="mt-3 min-h-11 rounded-md px-4 focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t('social.retry')}
          </button>
        </div>
      )}
      {rules && (
        <div
          className={props.variant === 'hall' ? 'min-w-0' : 'min-w-0'}
          lang={rules.contentLanguage}
        >
          {props.variant === 'hall' && (
            <div
              className="mb-4 space-y-2 border-b border-border pb-4"
              data-testid="tournament-hall-rules-summary"
            >
              <h3 className="mb-3 text-2xl font-semibold">
                {t('tournamentHall.editionRules')}
              </h3>
              <p>{rules.content.format}</p>
              {godRoleCategory && mechanicalWolfCategory && (
                <p>
                  {t('tournamentHall.categorySummary', {
                    godRoleLimit: godRoleCategory.limit,
                    mechanicalWolfLimit: mechanicalWolfCategory.limit,
                  })}
                </p>
              )}
              {rules.configuration.categories.length === 2 && (
                <p>{t('tournamentHall.alternation')}</p>
              )}
            </div>
          )}
          <div
            className={
              props.variant === 'hall'
                ? 'min-w-0 space-y-2'
                : 'min-w-0 space-y-2 rounded-xl border border-border bg-card p-2 sm:p-3'
            }
          >
            {visibleSectionIds.map(id => (
              <details
                key={id}
                data-section={id}
                data-testid={`tournament-rule-${id}`}
                open={id === 'eventRules'}
                onToggle={(event) => {
                  if (
                    props.variant === 'hall'
                    && event.currentTarget.open
                    && id !== selectedSection
                  ) {
                    selectSection(id)
                  }
                }}
                className="group rounded-lg border border-border bg-card"
              >
                <summary
                  onClick={() => selectSection(id)}
                  className="flex min-h-12 cursor-pointer list-none items-center justify-between gap-3 rounded-md px-4 py-3 font-semibold text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span>{t(`tournamentRules.${id}`)}</span>
                  <ChevronDown
                    className="size-5 shrink-0 text-primary transition-transform group-open:rotate-180"
                    aria-hidden="true"
                  />
                </summary>
                <div className="mx-3 border-t border-border px-1 py-3 text-sm leading-relaxed sm:text-base">
                  {id === 'eventRules' && (
                    <ul
                      className="space-y-3"
                      data-testid="tournament-public-event-rules"
                    >
                      {rules.body.eventRules.map((line, index) => (
                        <li key={index}>{line}</li>
                      ))}
                    </ul>
                  )}
                  {id === 'eventRules' && (
                    <div
                      data-section="configuration"
                      data-testid="tournament-rule-configuration"
                      className="mt-5 border-t border-border pt-5"
                    >
                      <h3 className="mb-3 font-semibold">
                        {t('tournamentRules.configuration')}
                      </h3>
                      <dl
                        className="space-y-3"
                        data-testid="tournament-public-configuration"
                      >
                        <div className="grid gap-1 sm:grid-cols-[8rem_minmax(0,1fr)]">
                          <dt>{t('tournamentRules.gameMode')}</dt>
                          <dd>{rules.body.configuration.gameMode}</dd>
                        </div>
                        <div className="grid gap-1 sm:grid-cols-[8rem_minmax(0,1fr)]">
                          <dt>{t('tournamentRules.roster')}</dt>
                          <dd>{rules.body.configuration.roster}</dd>
                        </div>
                        <div className="grid gap-1 sm:grid-cols-[8rem_minmax(0,1fr)]">
                          <dt>{t('tournamentRules.wolfRoster')}</dt>
                          <dd>{rules.body.configuration.wolfRoster}</dd>
                        </div>
                        <div className="grid gap-1 sm:grid-cols-[8rem_minmax(0,1fr)]">
                          <dt>{t('tournamentRules.godRoleChoices')}</dt>
                          <dd>{rules.body.configuration.godRoleChoices}</dd>
                        </div>
                      </dl>
                      <div
                        className="prose prose-sm mt-3 max-w-none break-words text-foreground [overflow-wrap:anywhere] prose-strong:text-foreground"
                        data-testid="tournament-public-configuration-rules"
                      >
                        <ReactMarkdown>
                          {rules.sections.find(
                            section => section.id === 'configuration',
                          )?.markdown ?? ''}
                        </ReactMarkdown>
                      </div>
                    </div>
                  )}
                  {id !== 'eventRules' && (
                    <div
                      className="prose prose-sm max-w-none break-words text-foreground [overflow-wrap:anywhere] prose-headings:font-semibold prose-headings:text-foreground prose-h2:text-lg prose-h3:text-base prose-h4:text-base prose-strong:text-foreground prose-li:my-1 [&_pre]:whitespace-pre-wrap"
                      data-testid={`tournament-public-${id}`}
                    >
                      <ReactMarkdown>
                        {rules.sections.find(section => section.id === id)
                          ?.markdown ?? ''}
                      </ReactMarkdown>
                    </div>
                  )}
                </div>
              </details>
            ))}
            {props.variant === 'hall' && (
              <button
                ref={fullRulesRef}
                type="button"
                onClick={props.onFullRules}
                data-testid="tournament-hall-full-rules"
                className="flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-border bg-primary/10 px-3 py-2 font-semibold text-primary transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {t('tournament.rules')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
